/**
 * DialerLeadContext.jsx — in-call lead context tooling.
 *
 * All components here are intentionally presentational + lifted via props so
 * the existing SalesDialer fixture wiring remains the single source of truth.
 *
 * Pieces:
 *   CampaignGroupedQueue — collapsible campaigns-first work queue.
 *   InCallLeadPanel       — persistent contact card + inline edits + Google
 *                            verify mock + AI suggestions + open-full-editor.
 *   MiniCampaignQueue     — compact "what's next in this campaign" chip.
 *   LeadFullEditorDrawer  — side drawer exposing every lead field.
 */
import React, { useMemo, useRef, useState } from "react";
import {
  Phone, ChevronDown, ChevronRight, Sparkles, Check, X, ExternalLink,
  ShieldCheck, Edit3, Search, Users, Target, Pencil, Zap, BookOpen, Play,
} from "lucide-react";
import { ContactListEditor, CampaignLearningPathV2 } from "./DialerDemoExtensions";
import CardTip from "@/components/CardTip";
// Iter 178.8 (Josh Feb 2026) — shared product taxonomy. Agent view
// uses the same chips + grouping as the admin Campaigns tab so reps
// browse campaigns by product line just like Danny / Josh do.
import {
  PRODUCT_CATEGORIES,
  PRODUCT_OTHER,
  categoryCodesForCampaign,
  getCategory,
} from "@/lib/productTaxonomy";

// Rating / priority palette — avatar tint + pill color per spec Section 1C.
// Hot=red, Warm=amber, Cold=blue — no gray fallback in the new design.
const RATING_PALETTE = {
  hot:  { bg: "#FCEBEB", text: "#A32D2D", pill: "#A32D2D", pillBg: "#FCEBEB" },
  warm: { bg: "#FAEEDA", text: "#854F0B", pill: "#854F0B", pillBg: "#FAEEDA" },
  cold: { bg: "#E6F1FB", text: "#185FA5", pill: "#185FA5", pillBg: "#E6F1FB" },
};
const paletteFor = (p) => RATING_PALETTE[p] || RATING_PALETTE.cold;

export function CampaignGroupedQueue({
  campaigns, workQueue, onStartCall, onOpenFullLead,
  onStartCampaign, // NEW: header "Start calling" CTA — parent owns smart-fallback (demo/real)
  apiBase, authToken, onNavigateToResources,
  queueRowsVisible = 4, // max queue rows per card (spec 1C: 4–5)
  chapterRowsVisible = 4, // max chapters shown (spec 1D)
  visitedLeadIds, // Iter 167 — Set of lead ids the rep has already opened today
  personalBanner = null, // Iter 178.12 — optional Personal Campaign card rendered above the chip strip
  personalCampaignEntry = null, // virtual "Personal Campaign" entry, always first in the campaign list
  loading = false, // show shimmer skeleton cards while campaigns + work queue load
}) {
  // Single-expand behavior: only one campaign expanded at a time per spec 1B.
  // Default to the first group (most relevant) once we know which campaigns
  // have leads. Persisted to sessionStorage so a refresh holds the rep's
  // expanded choice within the same tab.
  const [expandedId, setExpandedId] = useState(() => {
    try { return sessionStorage.getItem("dialer_expanded_campaign_id") || null; } catch (_) { return null; }
  });
  const [showAllByCampaign, setShowAllByCampaign] = useState({});
  const resourceSearchRefs = useRef({}); // cid -> <input>
  const [resourceHighlight, setResourceHighlight] = useState(null); // cid currently flashing a focus ring
  const [resourceFilters, setResourceFilters] = useState({}); // cid -> search string
  // Iter 178.8 (Josh Feb 2026) — agent-view product chip filter +
  // grouped-by-product layout, mirroring the admin Campaigns tab.
  // Default "all" shows every product group plus a separate Retarget
  // section at the bottom (matches Josh's reference screenshot).
  const [productFilter, setProductFilter] = useState("all");

  const setExpanded = (cid) => {
    const next = expandedId === cid ? null : cid;
    setExpandedId(next);
    try {
      if (next) sessionStorage.setItem("dialer_expanded_campaign_id", next);
      else sessionStorage.removeItem("dialer_expanded_campaign_id");
    } catch (_) {}
  };
  const toggleShowAll = (cid) => setShowAllByCampaign(p => ({ ...p, [cid]: !p[cid] }));
  const focusResourceSearch = (cid) => {
    setExpandedId(cid);
    try { sessionStorage.setItem("dialer_expanded_campaign_id", cid); } catch (_) {}
    // Let the expand render, then scroll + focus + flash ring.
    setTimeout(() => {
      const el = resourceSearchRefs.current[cid];
      if (el) { el.focus(); el.scrollIntoView({ behavior: "smooth", block: "center" }); }
      setResourceHighlight(cid);
      setTimeout(() => setResourceHighlight(prev => (prev === cid ? null : prev)), 1400);
    }, 50);
  };

  const groups = useMemo(() => {
    const byCampaign = new Map();
    workQueue.forEach(lead => {
      const cid = lead.campaign_id || "_uncat";
      if (!byCampaign.has(cid)) byCampaign.set(cid, []);
      byCampaign.get(cid).push(lead);
    });
    // Sort campaigns by active-first, then by pending lead count desc.
    // Iter 167 (Bug #8 — Danny report): also surface campaigns the user
    // is ASSIGNED to even when no queue items have been generated yet.
    // Peter reported "Agent View toggle should show assigned campaigns
    // and dial-out" — previously a campaign with 0 queue items was
    // completely invisible to its assigned agents, so they had no way
    // to start. Now those campaigns appear with an empty queue and a
    // "Start calling" CTA that triggers queue generation upstream.
    const assignedCampaigns = (campaigns || []).filter(c =>
      Array.isArray(c.agent_ids) && c.agent_ids.length > 0 && !byCampaign.has(c.id) && c.status === "active"
    );
    return (campaigns || [])
      .filter(c => byCampaign.has(c.id))
      .map(c => ({ campaign: c, leads: byCampaign.get(c.id) }))
      .concat(
        assignedCampaigns.map(c => ({ campaign: c, leads: [] }))
      )
      .concat(
        byCampaign.has("_uncat")
          ? [{ campaign: { id: "_uncat", name: "Uncategorized", status: "active" }, leads: byCampaign.get("_uncat") }]
          : []
      );
  }, [campaigns, workQueue]);

  // Auto-expand the first group on mount if none persisted — spec 1B:
  // "Only one campaign is expanded at a time by default."
  React.useEffect(() => {
    if (expandedId) return;
    if (groups.length === 0) return;
    setExpandedId(groups[0].campaign.id);
    // intentional: run once when groups resolve
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups.length]);

  // Iter 167 (Bug #8 — Danny report): only show the global empty-state
  // when there are no queue items AND no assigned-but-empty campaigns
  // surfaced via the groups list. Otherwise we'd hide Peter's assigned
  // campaign entirely.
  // Iter 178.8 — empty-state moved AFTER the displaySections useMemo
  // below (hooks rules: every hook must run on every render).

  // Iter 178.8 (Josh Feb 2026) — bucket groups by product category
  // and separate retarget campaigns into their own footer section
  // (per Josh's reference screenshot). A multi-product campaign
  // shows up under EACH product category it's tagged with so reps
  // browsing either category can find it. The chip filter rescopes
  // both regular product sections + the retarget footer.
  const { displaySections, retargetSection } = useMemo(() => {
    const isRetarget = (c) => Boolean(
      c?.is_retarget === true
      || c?.parent_campaign_id
      || c?.filters?.parent_campaign_id
    );
    const productBuckets = new Map();
    for (const cat of PRODUCT_CATEGORIES) productBuckets.set(cat.code, []);
    productBuckets.set(PRODUCT_OTHER.code, []);
    const retarget = [];
    for (const item of groups) {
      if (item.campaign.id === "_uncat") {
        productBuckets.get(PRODUCT_OTHER.code).push(item);
        continue;
      }
      if (isRetarget(item.campaign)) {
        retarget.push(item);
        continue;
      }
      const codes = categoryCodesForCampaign(item.campaign);
      for (const code of codes) {
        if (!productBuckets.has(code)) productBuckets.set(code, []);
        productBuckets.get(code).push(item);
      }
    }
    // Apply the product filter chip — keep only the selected category
    // (and its matching retarget items).
    const filterCode = productFilter;
    const sections = Array.from(productBuckets.entries())
      .map(([code, items]) => ({ category: getCategory(code), items }))
      .filter(s => s.items.length > 0)
      .filter(s => filterCode === "all" || s.category.code === filterCode);
    const filteredRetarget = retarget.filter(({ campaign }) => {
      if (filterCode === "all") return true;
      return categoryCodesForCampaign(campaign).includes(filterCode);
    });
    return { displaySections: sections, retargetSection: filteredRetarget };
  }, [groups, productFilter]);

  // Iter 178.8 — campaign render extracted so we can call it from
  // inside the section iteration without a 200-line copy/paste. All
  // the state we close over (expandedId, showAllByCampaign, etc.) is
  // local to the parent component.
  // Iter 178.8 — empty-state check moved to AFTER the displaySections
  // useMemo so the hook order is stable. Same semantic as before.
  // Skeleton loading — mirror the collapsed grouped-campaign cards while
  // both the campaigns and work-queue fetches are still in flight so the
  // rep never sees a false "work queue is empty" flash.
  if (loading) {
    return (
      <div data-testid="work-queue-loading" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {[0, 1, 2].map((i) => (
          <div key={i} className="card" data-testid={`work-queue-skeleton-${i}`} style={{ overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "0 16px", minHeight: 60 }}>
              <div className="skeleton" style={{ width: 14, height: 14, borderRadius: 4, flexShrink: 0 }} />
              <div className="skeleton" style={{ width: 14, height: 14, borderRadius: 4, flexShrink: 0 }} />
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
                <div className="skeleton" style={{ width: 200, height: 13, borderRadius: 4 }} />
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div className="skeleton" style={{ width: 120, height: 10, borderRadius: 4 }} />
                  <div className="skeleton" style={{ width: 160, height: 5, borderRadius: 3 }} />
                </div>
              </div>
              <div className="skeleton" style={{ width: 56, height: 18, borderRadius: 10 }} />
              <div className="skeleton" style={{ width: 96, height: 26, borderRadius: 8 }} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (workQueue.length === 0 && groups.length === 0) {
    return (
      <div data-testid="work-queue-empty" style={{ padding: "32px 20px", textAlign: "center", color: "var(--text-tertiary)", fontSize: 12, border: "1px dashed var(--border)", borderRadius: 10 }}>
        Your work queue is empty. Leads will appear here once assigned.
      </div>
    );
  }

  const renderCampaignCard = ({ campaign, leads }) => {
        const expanded = expandedId === campaign.id;
        const hot = leads.filter(l => l.priority === "hot").length;
        const warm = leads.filter(l => l.priority === "warm").length;
        const cold = leads.filter(l => l.priority === "cold").length;
        const showAll = !!showAllByCampaign[campaign.id];
        const visibleLeads = showAll ? leads : leads.slice(0, queueRowsVisible);
        const searchTerm = (resourceFilters[campaign.id] || "").trim();
        // Wave 2 / A8 — agent-view progress bar & start-calling guard.
        // `contacted` may live on the campaign doc (admin-synced) or be
        // inferable from the queue when items have moved to non-pending
        // states.  `total` falls back to the queue length so we never
        // crash on partial data.
        const campaignContacted = Number(campaign.contacted ?? 0);
        const campaignTotal = Number(campaign.leads ?? campaign.total_leads ?? leads.length ?? 0);
        const remaining = Math.max(0, campaignTotal - campaignContacted);
        const progressPct = campaignTotal > 0 ? Math.min(100, (campaignContacted / campaignTotal) * 100) : 0;
        const status = String(campaign.status || "active").toLowerCase();
        // Iter 192h (Lisa #e183fe09, also Joshua's "Start Dialing not
        // activated for me" June 2026). Previously this also disabled
        // when `leads.length === 0`, which meant any user whose personal
        // queue for the campaign was empty (super-admins, campaign owners
        // who hadn't added themselves as an agent, reps for a campaign
        // they're newly assigned to) saw a permanently grayed-out button.
        // But the backend already supports lazy queue generation —
        // `startCampaignCall` calls `/dialer/campaigns/{id}/generate-queue`
        // when `mine.length === 0` and surfaces a meaningful toast on
        // 403 (not on agent list) / 422 (no resolved audience). So the
        // pre-emptive disable was just hiding actionable feedback. Now
        // we only disable on terminal status (paused/completed/archived).
        const isStartDisabled = status === "paused" || status === "completed" || status === "archived";
        const tooltipForEnabled = leads.length === 0
          ? "Start calling — your queue will be generated for this campaign"
          : "Start calling the next lead";
        return (
          <div key={campaign.id} className="card" data-testid={`campaign-group-${campaign.id}`} style={{ overflow: "hidden", border: expanded ? "1px solid #B5D4F4" : undefined }}>
            {/* Header row — spec 1B: name + meta (when expanded) + badge + 2 CTAs */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: expanded ? "12px 16px" : "0 16px", minHeight: expanded ? undefined : 52, background: expanded ? "#F7FAFF" : "#fff" }}>
              <button
                data-testid={`campaign-group-toggle-${campaign.id}`}
                onClick={() => setExpanded(campaign.id)}
                style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 10, flex: 1, textAlign: "left", fontFamily: "var(--font)", padding: expanded ? 0 : "14px 0" }}>
                {expanded ? <ChevronDown style={{ width: 14, height: 14, color: "#888" }} /> : <ChevronRight style={{ width: 14, height: 14, color: "#888" }} />}
                <Target style={{ width: 14, height: 14, color: "#2563eb" }} />
                <div style={{ flex: 1, textAlign: "left" }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{campaign.name}</div>
                  {/* Wave 2 / A8 — Agent-view collapsed row shows remaining + progress. */}
                  {!expanded && (
                    <div style={{ marginTop: 4, display: "flex", alignItems: "center", gap: 8 }}>
                      <span data-testid={`campaign-remaining-${campaign.id}`} style={{ fontSize: 11, color: "#185FA5", fontWeight: 500 }}>
                        {remaining || leads.length} remaining to call
                      </span>
                      <div style={{ flex: 1, maxWidth: 180, height: 5, background: "#eee", borderRadius: 3, overflow: "hidden" }}>
                        <div
                          data-testid={`campaign-progress-${campaign.id}`}
                          style={{ height: 5, width: `${progressPct}%`, background: "#2563eb", borderRadius: 3, transition: "width 0.3s" }}
                        />
                      </div>
                      <span style={{ fontSize: 10, color: "#888" }}>{Math.round(progressPct)}%</span>
                    </div>
                  )}
                  {expanded && (
                    <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>
                      {leads.length} pending · {hot} hot · {warm} warm · {cold} cold
                      {campaign.type ? ` · ${campaign.type}` : ""}
                    </div>
                  )}
                </div>
              </button>
              <span style={{ padding: "3px 10px", borderRadius: 10, fontSize: 10, background: campaign.status === "active" ? "#DAFBE8" : "#f5f5f5", color: campaign.status === "active" ? "#1D9E75" : "#888", fontWeight: 600, textTransform: "uppercase" }}>{campaign.status}</span>
              <button
                data-testid={`campaign-start-calling-${campaign.id}`}
                onClick={(e) => { e.stopPropagation(); if (isStartDisabled) return; onStartCampaign?.(campaign, leads); }}
                disabled={isStartDisabled}
                title={isStartDisabled
                  ? `${campaign.status} — start unavailable`
                  : tooltipForEnabled}
                style={{
                  background: isStartDisabled ? "#E5E7EB" : "#2563eb",
                  color: isStartDisabled ? "#9CA3AF" : "#fff",
                  border: "none", borderRadius: 8, padding: "5px 14px", fontSize: 12, fontWeight: 500,
                  cursor: isStartDisabled ? "not-allowed" : "pointer",
                  fontFamily: "var(--font)", display: "inline-flex", alignItems: "center", gap: 5,
                }}>
                <Play style={{ width: 11, height: 11 }} /> Start calling
              </button>
              {campaign.course_id && (
                <button
                  data-testid={`campaign-resources-${campaign.id}`}
                  onClick={(e) => { e.stopPropagation(); focusResourceSearch(campaign.id); }}
                  style={{ background: "#fff", color: "#185FA5", border: "0.5px solid #ddd", borderRadius: 8, padding: "5px 14px", fontSize: 12, cursor: "pointer", fontFamily: "var(--font)", display: "inline-flex", alignItems: "center", gap: 5 }}>
                  <BookOpen style={{ width: 11, height: 11 }} /> Resources
                </button>
              )}
            </div>

            {expanded && (
              <div data-testid={`campaign-group-body-${campaign.id}`} style={{ borderTop: "0.5px solid #eee", display: "flex", alignItems: "stretch", background: "#fff" }}>
                {/* LEFT — Lead queue (flex:1, min-width:0) */}
                <div style={{ flex: 1, minWidth: 0, padding: "12px 16px" }}>
                  {/* Campaign detail stats row (kept from previous UX for managers) */}
                  {campaign.leads !== undefined && (
                    <div data-testid={`campaign-detail-${campaign.id}`} style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8, marginBottom: 12 }}>
                      {[
                        { label: "Total", val: campaign.leads, color: "#2563eb" },
                        { label: "Contacted", val: campaign.contacted, color: "#0071e3" },
                        { label: "Connected", val: campaign.connected, color: "#A78BFA" },
                        { label: "Meetings", val: campaign.meetings, color: "#1D9E75" },
                        { label: "Conv.", val: campaign.conversion, color: "#ff9500" },
                      ].map(m => (
                        <div key={m.label} style={{ background: "#fafafa", padding: "6px 8px", borderRadius: 6, border: "0.5px solid #eee" }}>
                          <div style={{ fontSize: 9, color: "#888", textTransform: "uppercase", letterSpacing: "0.3px" }}>{m.label}</div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: m.color, marginTop: 1 }}>{m.val ?? "—"}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                    <span style={{ fontSize: 10, color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px", flex: 1 }}>Your queue — prioritized by AI</span>
                    {leads.length > queueRowsVisible && (
                      <button
                        data-testid={`campaign-view-all-leads-${campaign.id}`}
                        onClick={() => toggleShowAll(campaign.id)}
                        style={{ fontSize: 11, color: "#185FA5", background: "none", border: "none", cursor: "pointer", padding: 0, fontFamily: "var(--font)" }}>
                        {showAll ? "Show top 4 ↑" : `View all ${leads.length} →`}
                      </button>
                    )}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    {visibleLeads.length === 0 && (
                      <div data-testid={`campaign-empty-queue-${campaign.id}`} style={{ padding: "14px 0", fontSize: 12, color: "#64748b" }}>
                        No leads queued yet for this campaign. Click <strong>Start calling</strong> above to generate your first batch.
                      </div>
                    )}
                    {visibleLeads.map(lead => {
                      const pal = paletteFor(lead.priority);
                      const initials = (lead.name || "").split(" ").map(n => n[0]).join("").slice(0, 2);
                      // Iter 167 (Bug #3 — Danny report): mute already-viewed
                      // leads so the rep can see at a glance which ones they
                      // already opened today. Doesn't disable the call —
                      // they can still re-dial intentionally if needed.
                      const viewed = visitedLeadIds && visitedLeadIds.has && visitedLeadIds.has(lead.id);
                      return (
                        <div key={lead.id} data-testid={`queue-lead-${lead.id}`} style={{ padding: "8px 0", borderBottom: "0.5px solid #eee", display: "flex", alignItems: "center", gap: 10, opacity: viewed ? 0.55 : 1 }}>
                          <div style={{ width: 32, height: 32, borderRadius: "50%", background: pal.bg, color: pal.text, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{initials}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <span style={{ fontSize: 13, fontWeight: 500 }}>{lead.name}</span>
                              {viewed && (
                                <span data-testid={`lead-viewed-${lead.id}`} title="You already opened this lead today" style={{ padding: "1px 6px", borderRadius: 10, fontSize: 9, background: "#f1f5f9", color: "#64748b", fontWeight: 600 }}>
                                  ✓ Viewed
                                </span>
                              )}
                              {lead.google_verified && (
                                <span data-testid={`google-verified-${lead.id}`} title="Google-verified contact" style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: "1px 5px", borderRadius: 10, fontSize: 8, background: "#E8F0FE", color: "#1967D2", fontWeight: 600 }}>
                                  <ShieldCheck style={{ width: 8, height: 8 }} /> Google
                                </span>
                              )}
                              <span style={{ padding: "1px 6px", borderRadius: 10, fontSize: 10, background: pal.pillBg, color: pal.pill, fontWeight: 600, textTransform: "capitalize" }}>{lead.priority}</span>
                            </div>
                            <div style={{ fontSize: 11, color: "#888", marginTop: 1 }}>
                              {lead.title ? `${lead.title} · ` : ""}{lead.practice} · {lead.phone} · AI {lead.aiScore} · {lead.attempts || 0} attempts
                              {lead.reason ? ` · ${lead.reason}` : ""}
                            </div>
                          </div>
                          <button onClick={() => onOpenFullLead?.(lead)} data-testid={`open-lead-${lead.id}`} style={{ border: "0.5px solid #B5D4F4", color: "#185FA5", background: "#fff", borderRadius: 8, padding: "4px 10px", fontSize: 11, cursor: "pointer", fontFamily: "var(--font)" }}>Open</button>
                          <button onClick={() => onStartCall(lead)} data-testid={`call-btn-${lead.id}`} style={{ background: "#2563eb", color: "#fff", border: "none", borderRadius: 8, padding: "4px 10px", fontSize: 11, cursor: "pointer", fontFamily: "var(--font)", display: "inline-flex", alignItems: "center", gap: 4 }}>
                            <Phone style={{ width: 10, height: 10 }} /> Call
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* RIGHT — Resources / Learning path (280px) */}
                {campaign.course_id && apiBase && onNavigateToResources ? (
                  <div data-testid={`campaign-lp-preview-${campaign.id}`} style={{ width: 280, flexShrink: 0, background: "#fafafa", borderLeft: "0.5px solid #eee", padding: "12px 14px" }}>
                    <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                      <span style={{ fontSize: 10, color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px", flex: 1 }}>Campaign resources</span>
                      <button
                        data-testid={`campaign-open-resources-${campaign.id}`}
                        onClick={() => onNavigateToResources({ courseId: campaign.course_id })}
                        style={{ fontSize: 11, color: "#185FA5", background: "none", border: "none", cursor: "pointer", padding: 0, fontFamily: "var(--font)" }}>
                        Open all →
                      </button>
                    </div>
                    <div style={{ position: "relative", marginBottom: 8, transition: "box-shadow 0.25s" }}>
                      <Search style={{ width: 11, height: 11, color: "#888", position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} />
                      <input
                        ref={(el) => { resourceSearchRefs.current[campaign.id] = el; }}
                        data-testid={`campaign-resources-search-${campaign.id}`}
                        type="text"
                        value={resourceFilters[campaign.id] || ""}
                        onChange={(e) => setResourceFilters(p => ({ ...p, [campaign.id]: e.target.value }))}
                        placeholder="Search billing codes, scripts, objections..."
                        style={{ width: "100%", border: resourceHighlight === campaign.id ? "1px solid #2563eb" : "0.5px solid #ddd", borderRadius: 8, padding: "7px 10px 7px 26px", fontSize: 12, fontFamily: "var(--font)", boxShadow: resourceHighlight === campaign.id ? "0 0 0 3px rgba(37,99,235,0.2)" : "none", outline: "none" }}
                      />
                    </div>
                    <CampaignLearningPathV2
                      campaign={campaign}
                      apiBase={apiBase}
                      authToken={authToken}
                      onNavigateToResources={onNavigateToResources}
                      externalSearch={searchTerm}
                      maxChapters={chapterRowsVisible}
                      compact
                    />
                  </div>
                ) : null}
              </div>
            )}
          </div>
        );
      };

  return (
    <div data-testid="campaign-grouped-queue" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {/* Iter 178.12 (Danny Feb 2026) — Personal Campaign banner
          rendered above the chip strip. Pinned to the top of the
          dialer queue per spec 1B. */}
      {personalBanner}
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "0 2px 4px 2px" }}>
        <Target style={{ width: 13, height: 13, color: "#2563eb" }} />
        <span style={{ fontSize: 12, fontWeight: 600, color: "#1B365D", flex: 1 }}>Your campaigns · prioritized by AI</span>
        <CardTip
          testId="campaign-queue-tip"
          title="Campaign overview"
          items={[
            "Campaigns are grouped by product/solution (CCM, RPM, APCM, AWV, EHR, RCM). Filter to a single product using the chips below.",
            "Each campaign expands into a 2-column view: your prioritized lead queue on the left, learning path resources on the right",
            "Click 'Start calling' to jump straight into the first lead of the campaign",
            "Retarget campaigns appear in their own section at the bottom — perfect for second-pass dialing",
          ]}
        />
      </div>
      {personalCampaignEntry}
      {/* Iter 178.8 (Josh Feb 2026) — agent-view product chip filter.
          Mirrors the admin Campaigns tab chips so the taxonomy is
          consistent between the two surfaces. */}
      <div
        data-testid="agent-product-chips"
        style={{ display: "flex", gap: 6, flexWrap: "wrap", margin: "0 2px 6px 2px" }}
      >
        <button
          type="button"
          data-testid="agent-product-chip-all"
          onClick={() => setProductFilter("all")}
          style={{
            padding: "5px 11px",
            borderRadius: 999,
            fontSize: 11,
            fontWeight: productFilter === "all" ? 600 : 500,
            background: productFilter === "all" ? "#0a2540" : "#fff",
            color: productFilter === "all" ? "#fff" : "#374151",
            border: `0.5px solid ${productFilter === "all" ? "#0a2540" : "#e5e7eb"}`,
            cursor: "pointer", fontFamily: "inherit",
            display: "inline-flex", alignItems: "center", gap: 5,
          }}
        >
          <span style={{ fontWeight: 700 }}>#</span> All
        </button>
        {PRODUCT_CATEGORIES.map(cat => {
          // Only show chips for categories that have at least 1 campaign
          // in the agent's current view — keeps the chip strip tight.
          const present = displaySections.some(s => s.category.code === cat.code)
            || groups.some(g => categoryCodesForCampaign(g.campaign).includes(cat.code));
          if (!present) return null;
          const active = productFilter === cat.code;
          return (
            <button
              key={cat.code}
              type="button"
              data-testid={`agent-product-chip-${cat.code}`}
              onClick={() => setProductFilter(active ? "all" : cat.code)}
              style={{
                padding: "5px 11px",
                borderRadius: 999,
                fontSize: 11,
                fontWeight: active ? 600 : 500,
                background: active ? cat.color : "#fff",
                color: active ? "#fff" : "#374151",
                border: `0.5px solid ${active ? cat.color : "#e5e7eb"}`,
                cursor: "pointer", fontFamily: "inherit",
                display: "inline-flex", alignItems: "center", gap: 5,
              }}
            >
              <span aria-hidden style={{ width: 6, height: 6, borderRadius: "50%", background: active ? "#fff" : cat.color, display: "inline-block" }} />
              {cat.code}
            </button>
          );
        })}
      </div>
      {displaySections.map(section => (
        <div key={`section-${section.category.code}`} data-testid={`agent-product-section-${section.category.code}`}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 2px 6px 2px" }}>
            <span aria-hidden style={{ width: 8, height: 8, borderRadius: "50%", background: section.category.color, flexShrink: 0 }} />
            <span style={{ fontSize: 10, fontWeight: 600, color: "#475569", letterSpacing: "0.08em", textTransform: "uppercase" }}>
              {section.category.label}
            </span>
            <div style={{ flex: 1, height: 1, background: "linear-gradient(to right, #e5e7eb 0%, transparent 100%)" }} />
          </div>
          {section.items.map(renderCampaignCard)}
        </div>
      ))}
      {retargetSection.length > 0 && (
        <div data-testid="agent-retarget-section">
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 2px 6px 2px" }}>
            <span aria-hidden style={{ width: 8, height: 8, borderRadius: "50%", background: "#7C3AED", flexShrink: 0 }} />
            <span style={{ fontSize: 10, fontWeight: 600, color: "#5B21B6", letterSpacing: "0.08em", textTransform: "uppercase" }}>
              Retarget Campaigns
            </span>
            <div style={{ flex: 1, height: 1, background: "linear-gradient(to right, #ddd6fe 0%, transparent 100%)" }} />
          </div>
          {retargetSection.map(renderCampaignCard)}
        </div>
      )}
      {displaySections.length === 0 && retargetSection.length === 0 && (
        <div data-testid="agent-product-empty" style={{ padding: "20px", textAlign: "center", color: "#94a3b8", fontSize: 12, fontStyle: "italic" }}>
          No campaigns in this product yet. Switch chips or clear the filter to see your full queue.
        </div>
      )}
    </div>
  );
}

// Mini queue chip shown during an active call — tells the rep what's next.
export function MiniCampaignQueue({ campaignId, campaignName, workQueue, selectedLeadId, onStartCall, powerDialOn, onPowerDialToggle }) {
  const rows = workQueue.filter(l => l.campaign_id === campaignId);
  const remaining = rows.filter(l => l.id !== selectedLeadId);
  const next = remaining[0];
  return (
    <div data-testid="mini-campaign-queue" className="card" style={{ padding: "10px 14px", display: "flex", alignItems: "center", gap: 10, marginBottom: 10, background: "#FFF8E6", borderLeft: "3px solid #FFB84D" }}>
      <Target style={{ width: 12, height: 12, color: "#854F0B" }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: "#854F0B" }}>{campaignName || "Campaign"} · {remaining.length} remaining</div>
        <div style={{ fontSize: 10, color: "#6B3F00", marginTop: 1 }}>
          {next ? <>Next: <strong>{next.name}</strong> — {next.practice}</> : "No more leads in this campaign"}
        </div>
      </div>
      {onPowerDialToggle && (
        <label data-testid="power-dial-toggle" style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10, fontWeight: 600, color: powerDialOn ? "#854F0B" : "#888", cursor: "pointer", padding: "3px 8px", borderRadius: 14, background: powerDialOn ? "#FDE68A" : "#fff", border: powerDialOn ? "0.5px solid #F59E0B" : "0.5px solid #FFD699" }}>
          <input type="checkbox" checked={!!powerDialOn} onChange={e => onPowerDialToggle(e.target.checked)} />
          <Zap style={{ width: 9, height: 9 }} /> Power Dial
        </label>
      )}
      {next && (
        <button data-testid="mini-queue-next-btn" onClick={() => onStartCall(next)} className="btn" style={{ fontSize: 10, padding: "4px 10px" }}>
          <Phone style={{ width: 10, height: 10 }} /> Next
        </button>
      )}
    </div>
  );
}

// Compact, collapsible shell around ContactListEditor used inside the
// in-call panel. Shows a count chip + Add-contact shortcut; toggles open
// to reveal the full multi-contact form (primary, secondary, typed phones
// and emails, roles/titles). Hydrates an implicit primary contact from
// flat lead fields the first time it's opened so reps don't see an empty
// list mid-call.
function InCallContactsEditorInline({ lead, onChange }) {
  // Wrap `contacts` in its own memo so the identity is stable across
  // renders; otherwise the `hydrated` memo below would re-run whenever the
  // parent re-renders with a fresh lead object (ESLint flags this).
  const contacts = React.useMemo(() => lead?.contacts || [], [lead?.contacts]);
  const hydrated = React.useMemo(() => {
    if (contacts.length > 0) return contacts;
    if (!lead?.name && !lead?.phone && !lead?.email) return contacts;
    return [{
      id: "c-primary",
      name: lead.name || lead.contact_name || "",
      title: lead.title || lead.contact_role || "",
      phones: lead.phone ? [{ id: "p-1", label: "primary", number: lead.phone }] : [],
      emails: lead.email ? [{ id: "e-1", label: "work", address: lead.email }] : [],
    }];
  }, [lead, contacts]);
  const [open, setOpen] = React.useState(contacts.length > 1);
  return (
    <div style={{ border: "0.5px solid #eee", borderRadius: 8, background: "#fff" }}>
      <button
        data-testid="in-call-contacts-editor-toggle"
        onClick={() => setOpen(v => !v)}
        style={{ width: "100%", background: "transparent", border: "none", padding: "9px 12px", display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontFamily: "var(--font)" }}
      >
        <Users style={{ width: 11, height: 11, color: "#2563eb" }} />
        <span style={{ fontSize: 11, fontWeight: 600, color: "#1B365D" }}>Contacts</span>
        <span style={{ fontSize: 9, padding: "1px 7px", borderRadius: 10, background: "#E6F1FB", color: "#185FA5", fontWeight: 700 }}>{hydrated.length}</span>
        <CardTip
          testId="in-call-contacts-tip"
          as="span"
          title="Multi-contact editor"
          items={[
            "Capture additional people at the practice live (decision makers, office admins, referrers)",
            "Typed phones: cell, office, fax, direct, work",
            "Typed emails: work, billing, personal, assistant",
            "Primary contact drives the header badge and dialer",
          ]}
        />
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 10, color: "#2563eb", fontWeight: 600 }}>{open ? "Hide" : "Edit"}</span>
        {open ? <ChevronDown style={{ width: 11, height: 11, color: "#2563eb" }} /> : <ChevronRight style={{ width: 11, height: 11, color: "#2563eb" }} />}
      </button>
      {open && (
        <div data-testid="in-call-contacts-editor-body" style={{ padding: "4px 12px 12px 12px", borderTop: "0.5px solid #eee" }}>
          <ContactListEditor contacts={hydrated} onChange={onChange} compact />
        </div>
      )}
    </div>
  );
}


// Inline contact card — ALWAYS visible during a call. Core fields editable
// here; "Open full editor" pops a drawer with every lead field.
export function InCallLeadPanel({
  lead, onChange, onOpenFullEditor, onGoogleVerify, googleVerifying,
  aiSuggestions, onApplySuggestion, onDismissSuggestion,
  autoApplyAi, onToggleAutoApplyAi,
}) {
  if (!lead) return null;
  const setField = (k, v) => onChange?.({ ...lead, [k]: v });
  const inputStyle = { width: "100%", border: "0.5px solid #ddd", borderRadius: 6, padding: "6px 8px", fontSize: 12, fontFamily: "var(--font)", marginTop: 3 };

  return (
    <div data-testid="in-call-lead-panel" className="card" style={{ padding: "14px 16px", marginBottom: 12, borderLeft: "3px solid var(--blue)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <Users style={{ width: 12, height: 12, color: "#2563eb" }} />
        <div style={{ fontSize: 12, fontWeight: 600, flex: 1 }}>Lead contact · edit any field live</div>
        <CardTip
          testId="in-call-lead-panel-tip"
          title="Live lead panel"
          items={[
            "Every field is click-to-edit — saved as you type",
            "Google verification badge appears once the lead is confirmed",
            "All fields drawer gives access to 80+ fields",
            "AI transcript updates silently fill in new details you mention",
          ]}
        />
        {lead.google_verified && (
          <span data-testid="in-call-google-badge" style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: "2px 8px", borderRadius: 10, fontSize: 9, background: "#E8F0FE", color: "#1967D2", fontWeight: 600 }}>
            <ShieldCheck style={{ width: 10, height: 10 }} /> Google verified
          </span>
        )}
        <button data-testid="google-verify-btn" onClick={onGoogleVerify} disabled={googleVerifying}
          className="btn" style={{ fontSize: 10, opacity: googleVerifying ? 0.5 : 1 }}>
          <Search style={{ width: 10, height: 10 }} /> {lead.google_verified ? "Re-verify" : "Verify with Google"}
        </button>
        <button data-testid="open-full-editor-btn" onClick={onOpenFullEditor} className="btn" style={{ fontSize: 10 }}>
          <Pencil style={{ width: 10, height: 10 }} /> All fields
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div>
          <label style={{ fontSize: 10, color: "#888", fontWeight: 600 }}>Name</label>
          <input data-testid="lead-field-name" value={lead.name || ""} onChange={e => setField("name", e.target.value)} style={inputStyle} />
        </div>
        <div>
          <label style={{ fontSize: 10, color: "#888", fontWeight: 600 }}>Title</label>
          <input data-testid="lead-field-title" value={lead.title || ""} onChange={e => setField("title", e.target.value)} style={inputStyle} />
        </div>
        <div>
          <label style={{ fontSize: 10, color: "#888", fontWeight: 600 }}>Phone</label>
          <input data-testid="lead-field-phone" value={lead.phone || ""} onChange={e => setField("phone", e.target.value)} style={inputStyle} />
        </div>
        <div>
          <label style={{ fontSize: 10, color: "#888", fontWeight: 600 }}>Email</label>
          <input data-testid="lead-field-email" value={lead.email || ""} onChange={e => setField("email", e.target.value)} style={inputStyle} />
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <label style={{ fontSize: 10, color: "#888", fontWeight: 600 }}>Practice</label>
          <input data-testid="lead-field-practice" value={lead.practice || ""} onChange={e => setField("practice", e.target.value)} style={inputStyle} />
        </div>
        <div>
          <label style={{ fontSize: 10, color: "#888", fontWeight: 600 }}>Specialty</label>
          <input data-testid="lead-field-specialty" value={lead.specialty || ""} onChange={e => setField("specialty", e.target.value)} style={inputStyle} />
        </div>
        <div>
          <label style={{ fontSize: 10, color: "#888", fontWeight: 600 }}>Providers</label>
          <input data-testid="lead-field-providers" type="number" value={lead.providers ?? ""} onChange={e => setField("providers", parseInt(e.target.value || "0", 10))} style={inputStyle} />
        </div>
      </div>

      {/* Inline multi-contact editor — lets reps add secondary contacts
         (decision makers, office admins, referrers) live during a call.
         Collapsed by default to keep screen real estate tight; the count
         chip shows how many contacts are on file. */}
      <div data-testid="in-call-contacts-editor" style={{ marginTop: 12 }}>
        <InCallContactsEditorInline
          lead={lead}
          onChange={(contacts) => onChange?.({ ...lead, contacts })}
        />
      </div>

      {/* AI-suggested edits (Option A) + Auto-apply toggle (Option B) */}
      <div data-testid="ai-suggestions-section" style={{ marginTop: 12, padding: "10px 12px", background: "#F5F0FE", borderRadius: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
          <Sparkles style={{ width: 11, height: 11, color: "#7C3AED" }} />
          <span style={{ fontSize: 11, fontWeight: 600, color: "#5B21B6", flex: 1 }}>AI transcript updates</span>
          <CardTip
            testId="ai-copilot-tip"
            title="AI co-pilot"
            items={[
              "Listens to the live call transcript (demo: simulated)",
              "Extracts budget, timeline, pain points, decision makers automatically",
              "Auto-apply is ON in demo — suggestions merge into the lead profile silently",
            ]}
          />
          <span data-testid="ai-auto-apply-always-on" style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10, color: "#5B21B6", fontWeight: 600 }}>
            <Sparkles style={{ width: 9, height: 9 }} />
            Auto-apply: ON
          </span>
        </div>
        {(!aiSuggestions || aiSuggestions.length === 0) && (
          <div data-testid="ai-suggestions-empty" style={{ fontSize: 10, color: "#7E6DAE" }}>
            Listening… the AI will silently update the lead profile as it hears key details.
          </div>
        )}
        {aiSuggestions && aiSuggestions.map((s, i) => (
          <div key={`${s.field}-${i}`} data-testid={`ai-suggestion-${s.field}`} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "6px 0", borderTop: i === 0 ? "none" : "0.5px dashed #D4C3F7" }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#3C1073" }}>{s.field}: <span style={{ fontWeight: 500 }}>{Array.isArray(s.value) ? s.value.join(", ") : String(s.value)}</span></div>
              <div style={{ fontSize: 9, color: "#7E6DAE", marginTop: 1 }}>{s.reason}</div>
            </div>
            <button data-testid={`ai-apply-${s.field}`} onClick={() => onApplySuggestion?.(s)} className="btn" style={{ fontSize: 10, padding: "3px 8px", background: "#7C3AED", color: "#fff" }}>
              <Check style={{ width: 10, height: 10 }} /> Apply
            </button>
            <button data-testid={`ai-dismiss-${s.field}`} onClick={() => onDismissSuggestion?.(s)} className="btn" style={{ fontSize: 10, padding: "3px 8px" }}>
              <X style={{ width: 10, height: 10 }} /> Dismiss
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// Inline Lead Full View (demo-only). Opened from the Sales Dialer so the user
// stays inside the module; onClose returns to the active call / queue.
export function DemoLeadFullView({ lead, timeline, onClose, onChange, onStartCall, isOnCall, onPowerDialToggle, powerDialOn }) {
  if (!lead) return null;
  const setField = (k, v) => onChange?.({ ...lead, [k]: v });
  const I = { width: "100%", border: "0.5px solid #ddd", borderRadius: 6, padding: "7px 10px", fontSize: 12, fontFamily: "var(--font)", marginTop: 3 };
  return (
    <div data-testid="demo-lead-full-view" style={{ background: "#fff", borderRadius: 10, border: "0.5px solid #eee" }}>
      {/* Sticky header — return control + power dial + call launcher */}
      <div style={{ padding: "14px 20px", borderBottom: "0.5px solid #eee", display: "flex", alignItems: "center", gap: 10 }}>
        <button data-testid="lead-full-back-btn" onClick={onClose} className="btn" style={{ fontSize: 11 }}>
          <ChevronRight style={{ width: 12, height: 12, transform: "rotate(180deg)" }} /> {isOnCall ? "Back to call" : "Back to queue"}
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
            {lead.name}
            {lead.google_verified && (
              <span data-testid="full-view-google-badge" style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: "2px 8px", borderRadius: 10, fontSize: 9, background: "#E8F0FE", color: "#1967D2", fontWeight: 600 }}>
                <ShieldCheck style={{ width: 10, height: 10 }} /> Google verified
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>{lead.title || ""}{lead.title ? " · " : ""}{lead.practice} · {lead.phone}</div>
        </div>
        {onPowerDialToggle && (
          <label data-testid="power-dial-toggle-header" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 600, color: powerDialOn ? "#854F0B" : "#888", cursor: "pointer", padding: "4px 10px", borderRadius: 16, background: powerDialOn ? "#FEF3C7" : "#f5f5f5", border: powerDialOn ? "0.5px solid #F59E0B" : "0.5px solid transparent" }}>
            <input type="checkbox" checked={!!powerDialOn} onChange={e => onPowerDialToggle(e.target.checked)} />
            <Zap style={{ width: 10, height: 10 }} /> Power Dial
          </label>
        )}
        {!isOnCall && (
          <button data-testid="lead-full-call-btn" onClick={() => onStartCall?.(lead)} className="btn btn-primary" style={{ fontSize: 11 }}>
            <Phone style={{ width: 10, height: 10 }} /> Call
          </button>
        )}
      </div>

      {/* Body — two columns: fields (left) + timeline (right) */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: 0 }}>
        <div style={{ padding: 20, borderRight: "0.5px solid #eee" }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#555", marginBottom: 8 }}>Core info</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {[
              ["name","Name"],["title","Title"],["phone","Phone"],["email","Email"],
              ["practice","Practice"],["website","Website"],
              ["specialty","Specialty"],["providers","Providers","number"],
              ["current_ehr","Current EHR"],["annual_revenue","Annual revenue"],
              ["budget","Budget"],["timeline","Timeline"],
              ["stage","Stage"],["aiScore","AI score","number"],
              ["city","City"],["state","State"],
              ["timezone","Timezone"],["practice_type","Practice type"],
              ["lead_source","Lead source"],["assigned_rep","Assigned rep"],
              ["deal_value","Deal value","number"],["close_probability","Close probability (%)","number"],
              ["competitor","Competitor (if any)"],["calls_made","Calls made","number"],
            ].map(([k, label, t]) => (
              <div key={k}>
                <label style={{ fontSize: 10, color: "#888", fontWeight: 600 }}>{label}</label>
                <input data-testid={`full-field-${k}`} type={t || "text"} value={lead[k] ?? ""} onChange={e => setField(k, t === "number" ? parseInt(e.target.value || "0", 10) : e.target.value)} style={I} />
              </div>
            ))}
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={{ fontSize: 10, color: "#888", fontWeight: 600 }}>Address</label>
              <input data-testid="full-field-address" value={lead.address || ""} onChange={e => setField("address", e.target.value)} style={I} />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={{ fontSize: 10, color: "#888", fontWeight: 600 }}>Reason (context)</label>
              <input data-testid="full-field-reason" value={lead.reason || ""} onChange={e => setField("reason", e.target.value)} style={I} />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={{ fontSize: 10, color: "#888", fontWeight: 600 }}>Decision makers (comma separated)</label>
              <input data-testid="full-field-decision_makers" value={(lead.decision_makers || []).join(", ")} onChange={e => setField("decision_makers", e.target.value.split(",").map(s => s.trim()).filter(Boolean))} style={I} />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={{ fontSize: 10, color: "#888", fontWeight: 600 }}>Pain points (comma separated)</label>
              <input data-testid="full-field-pain_points" value={(lead.pain_points || []).join(", ")} onChange={e => setField("pain_points", e.target.value.split(",").map(s => s.trim()).filter(Boolean))} style={I} />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={{ fontSize: 10, color: "#888", fontWeight: 600 }}>Products of interest (comma separated)</label>
              <input data-testid="full-field-products_of_interest" value={(lead.products_of_interest || []).join(", ")} onChange={e => setField("products_of_interest", e.target.value.split(",").map(s => s.trim()).filter(Boolean))} style={I} />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={{ fontSize: 10, color: "#888", fontWeight: 600 }}>Tags (comma separated)</label>
              <input data-testid="full-field-tags" value={(lead.tags || []).join(", ")} onChange={e => setField("tags", e.target.value.split(",").map(s => s.trim()).filter(Boolean))} style={I} />
            </div>
          </div>

          {/* Multi-contact editor — primary/secondary phones & emails per contact */}
          <div style={{ marginTop: 22, paddingTop: 16, borderTop: "0.5px solid #eee" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <Users style={{ width: 12, height: 12, color: "#2563eb" }} />
              <div style={{ fontSize: 11, fontWeight: 600, color: "#555" }}>Contacts on file</div>
              <span style={{ fontSize: 10, color: "#888" }}>· primary/secondary with typed phones (cell · office · fax · direct) and emails</span>
            </div>
            <ContactListEditor
              contacts={lead.contacts || []}
              onChange={(nextContacts) => setField("contacts", nextContacts)}
            />
          </div>
        </div>
        {/* Timeline column */}
        <div style={{ padding: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 10, color: "#555" }}>Activity timeline</div>
          {(!timeline || timeline.length === 0) && (
            <div style={{ fontSize: 11, color: "#888", padding: 12, background: "#fafafa", borderRadius: 6 }}>No recorded activity yet.</div>
          )}
          <div data-testid="full-view-timeline" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {(timeline || []).map(t => (
              <div key={t.id} data-testid={`full-view-timeline-${t.id}`} style={{ display: "flex", gap: 8, padding: "8px 10px", background: "#fafafa", borderRadius: 6 }}>
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#2563eb", marginTop: 5, flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11 }}>{t.desc}</div>
                  <div style={{ fontSize: 9, color: "#888" }}>
                    {t.time ? new Date(t.time).toLocaleString() : ""} · {t.by}
                  </div>
                </div>
                <span style={{ padding: "1px 6px", borderRadius: 10, fontSize: 8, background: "#eef", color: "#2563eb", fontWeight: 600, textTransform: "uppercase" }}>{t.type}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Full-fields drawer — right-side overlay with EVERY lead field.
export function LeadFullEditorDrawer({ lead, onChange, onClose, timeline, onOpenFullLeadView }) {
  if (!lead) return null;
  const setField = (k, v) => onChange?.({ ...lead, [k]: v });
  const allFields = [
    { key: "name", label: "Name" },
    { key: "title", label: "Title" },
    { key: "phone", label: "Phone" },
    { key: "email", label: "Email" },
    { key: "practice", label: "Practice" },
    { key: "website", label: "Website" },
    { key: "address", label: "Address" },
    { key: "specialty", label: "Specialty" },
    { key: "providers", label: "Providers", type: "number" },
    { key: "current_ehr", label: "Current EHR" },
    { key: "annual_revenue", label: "Annual revenue" },
    { key: "budget", label: "Budget" },
    { key: "timeline", label: "Timeline" },
    { key: "stage", label: "Stage" },
    { key: "aiScore", label: "AI score", type: "number" },
    { key: "reason", label: "Reason" },
  ];
  const inputStyle = { width: "100%", border: "0.5px solid #ddd", borderRadius: 6, padding: "6px 8px", fontSize: 12, fontFamily: "var(--font)", marginTop: 3 };
  return (
    <div data-testid="lead-full-editor-drawer" style={{ position: "fixed", top: 0, right: 0, width: 460, height: "100vh", background: "#fff", boxShadow: "-4px 0 16px rgba(0,0,0,0.12)", zIndex: 80, display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "14px 18px", borderBottom: "0.5px solid #eee", display: "flex", alignItems: "center", gap: 10 }}>
        <Edit3 style={{ width: 14, height: 14, color: "#2563eb" }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>Edit all lead fields</div>
          <div style={{ fontSize: 10, color: "#888" }}>{lead.name} · {lead.practice}</div>
        </div>
        <button data-testid="drawer-open-full-lead" onClick={onOpenFullLeadView} className="btn" style={{ fontSize: 10 }}>
          <ExternalLink style={{ width: 10, height: 10 }} /> Full view
        </button>
        <button data-testid="drawer-close-btn" onClick={onClose} className="btn" style={{ fontSize: 10 }}>
          <X style={{ width: 10, height: 10 }} /> Close
        </button>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: 18 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {allFields.map(f => (
            <div key={f.key} style={{ gridColumn: (f.key === "address" || f.key === "reason") ? "1 / -1" : "auto" }}>
              <label style={{ fontSize: 10, color: "#888", fontWeight: 600 }}>{f.label}</label>
              <input data-testid={`drawer-field-${f.key}`} type={f.type || "text"} value={lead[f.key] ?? ""} onChange={e => setField(f.key, f.type === "number" ? parseInt(e.target.value || "0", 10) : e.target.value)} style={inputStyle} />
            </div>
          ))}
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={{ fontSize: 10, color: "#888", fontWeight: 600 }}>Decision makers (comma separated)</label>
            <input data-testid="drawer-field-decision_makers" value={(lead.decision_makers || []).join(", ")} onChange={e => setField("decision_makers", e.target.value.split(",").map(s => s.trim()).filter(Boolean))} style={inputStyle} />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={{ fontSize: 10, color: "#888", fontWeight: 600 }}>Pain points (comma separated)</label>
            <input data-testid="drawer-field-pain_points" value={(lead.pain_points || []).join(", ")} onChange={e => setField("pain_points", e.target.value.split(",").map(s => s.trim()).filter(Boolean))} style={inputStyle} />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={{ fontSize: 10, color: "#888", fontWeight: 600 }}>Tags (comma separated)</label>
            <input data-testid="drawer-field-tags" value={(lead.tags || []).join(", ")} onChange={e => setField("tags", e.target.value.split(",").map(s => s.trim()).filter(Boolean))} style={inputStyle} />
          </div>
        </div>

        {/* Activity timeline preview */}
        {timeline && timeline.length > 0 && (
          <div style={{ marginTop: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 8, color: "#555" }}>Recent activity</div>
            <div data-testid="drawer-timeline" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {timeline.slice(0, 8).map(t => (
                <div key={t.id} style={{ display: "flex", gap: 8, padding: "6px 8px", background: "#fafafa", borderRadius: 6 }}>
                  <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#2563eb", marginTop: 5, flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11 }}>{t.desc}</div>
                    <div style={{ fontSize: 9, color: "#888" }}>{t.time ? new Date(t.time).toLocaleString() : ""} · {t.by}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
